import classNames from "classnames";
import React, { ComponentType, MouseEventHandler, useMemo } from "react";
import { FrontdoorLogoColor, FrontdoorLogomarkColor, FrontdoorLogomarkMono, FrontdoorLogoMono } from "./logos";
import Ahs50YearsLogoColor from "./logos/ahs-50years-logo-color";
import Ahs50YearsLogoMono from "./logos/ahs-50years-logo-mono";
import AhsLogoColor from "./logos/ahs-logo-color";
import AhsLogoMono from "./logos/ahs-logo-mono";
import AhsLogomarkColor from "./logos/ahs-logomark-color";
import AhsLogomarkMono from "./logos/ahs-logomark-mono";
import AhsLogotypeColor from "./logos/ahs-logotype-color";
import AhsLogotypeMono from "./logos/ahs-logotype-mono";
import AhsProconnectLogoColor from "./logos/ahs-proconnect-logo-color";
import AhsProconnectLogoMono from "./logos/ahs-proconnect-logo-mono";
import AhsProconnectLogotypeColor from "./logos/ahs-proconnect-logotype-color";
import AhsProconnectLogotypeMono from "./logos/ahs-proconnect-logotype-mono";
import AhsWarrantylinkLogoColor from "./logos/ahs-warrantylink-logo-color";
import AhsWarrantylinkLogoMono from "./logos/ahs-warrantylink-logo-mono";
import HsaLogoColor from "./logos/hsa-logo-color";
import HsaLogoMono from "./logos/hsa-logo-mono";
import HsaProconnectLogoColor from "./logos/hsa-proconnect-logo-color";
import HsaProconnectLogoMono from "./logos/hsa-proconnect-logo-mono";
import HsaProconnectLogotypeColor from "./logos/hsa-proconnect-logotype-color";
import HsaProconnectLogotypeMono from "./logos/hsa-proconnect-logotype-mono";
import LandmarkLogoColor from "./logos/landmark-logo-color";
import LandmarkLogoMono from "./logos/landmark-logo-mono";
import LandmarkLogomarkColor from "./logos/landmark-logomark-color";
import LandmarkLogomarkMono from "./logos/landmark-logomark-mono";
import OneguardLogoColor from "./logos/oneguard-logo-color";
import OneguardLogoMono from "./logos/oneguard-logo-mono";
import OneguardLogomarkColor from "./logos/oneguard-logomark-color";
import OneguardLogomarkMono from "./logos/oneguard-logomark-mono";
import ProconnectLogoColor from "./logos/proconnect-logo-color";
import ProconnectLogoMono from "./logos/proconnect-logo-mono";
import { Brand } from "./types";

export type LogoVariant = "logo" | "logo-mark" | "logo-type";

const logoComponentMap: {
  [brand in Brand]: { [variant in LogoVariant]: { [color in "color" | "mono"]: ComponentType | null } };
} = {
  frontdoor: {
    logo: {
      color: FrontdoorLogoColor,
      mono: FrontdoorLogoMono,
    },
    "logo-mark": {
      color: FrontdoorLogomarkColor,
      mono: FrontdoorLogomarkMono,
    },
    "logo-type": {
      color: null,
      mono: null,
    },
  },
  ahs: {
    logo: {
      color: AhsLogoColor,
      mono: AhsLogoMono,
    },
    "logo-mark": {
      color: AhsLogomarkColor,
      mono: AhsLogomarkMono,
    },
    "logo-type": {
      color: AhsLogotypeColor,
      mono: AhsLogotypeMono,
    },
  },
  "ahs-50years": {
    logo: {
      color: Ahs50YearsLogoColor,
      mono: Ahs50YearsLogoMono,
    },
    "logo-mark": {
      color: Ahs50YearsLogoColor,
      mono: Ahs50YearsLogoMono,
    },
    "logo-type": {
      color: Ahs50YearsLogoColor,
      mono: Ahs50YearsLogoMono,
    },
  },
  hsa: {
    logo: {
      color: HsaLogoColor,
      mono: HsaLogoMono,
    },
    "logo-mark": {
      color: null,
      mono: null,
    },
    "logo-type": {
      color: null,
      mono: null,
    },
  },
  oneguard: {
    logo: {
      color: OneguardLogoColor,
      mono: OneguardLogoMono,
    },
    "logo-mark": {
      color: OneguardLogomarkColor,
      mono: OneguardLogomarkMono,
    },
    "logo-type": {
      color: null,
      mono: null,
    },
  },
  landmark: {
    logo: {
      color: LandmarkLogoColor,
      mono: LandmarkLogoMono,
    },
    "logo-mark": {
      color: LandmarkLogomarkColor,
      mono: LandmarkLogomarkMono,
    },
    "logo-type": {
      color: null,
      mono: null,
    },
  },
  "ahs-warranty-link": {
    logo: {
      color: AhsWarrantylinkLogoColor,
      mono: AhsWarrantylinkLogoMono,
    },
    "logo-mark": {
      color: null,
      mono: null,
    },
    "logo-type": {
      color: null,
      mono: null,
    },
  },
  "ahs-proconnect": {
    logo: {
      color: AhsProconnectLogoColor,
      mono: AhsProconnectLogoMono,
    },
    "logo-mark": {
      color: null,
      mono: null,
    },
    "logo-type": {
      color: AhsProconnectLogotypeColor,
      mono: AhsProconnectLogotypeMono,
    },
  },
  "hsa-proconnect": {
    logo: {
      color: HsaProconnectLogoColor,
      mono: HsaProconnectLogoMono,
    },
    "logo-mark": {
      color: null,
      mono: null,
    },
    "logo-type": {
      color: HsaProconnectLogotypeColor,
      mono: HsaProconnectLogotypeMono,
    },
  },
  proconnect: {
    logo: {
      color: ProconnectLogoColor,
      mono: ProconnectLogoMono,
    },
    "logo-mark": {
      color: null,
      mono: null,
    },
    "logo-type": {
      color: null,
      mono: null,
    },
  },
};

export interface LogoProps {
  /**
   * This prop specifies the Frontdoor brand for which the logo is to be rendered.
   *
   * @default AHS
   */
  brand?: Brand;

  /**
   * This prop controls the visual variant of the logo that is rendered.
   * `logo` renders the full logo.
   * `logo-mark` renders only the brand mark / icon / graphic portion of the logo.
   * `logo-type` renders only the textual brand name portion of the logo.
   *
   * @default logo
   */
  variant?: LogoVariant;

  /**
   * If this prop is `true` then the logo is rendered using a monochrome graphic.
   *
   * @default false
   */
  monochrome?: boolean;

  /**
   * If this prop is `true` then the logo is rendered using a graphic suited for rendering on a dark background.
   *
   * @default false
   */
  inverse?: boolean;

  /**
   * This event is attached to the `onClick` of the SVG Logo that is rendered.
   *
   * If both `onClick` and `href` are passed then the `onClick` is applied to the <a> element added as a result of the
   * `href` prop.
   */
  onClick?: MouseEventHandler<SVGElement | HTMLAnchorElement>;

  /**
   * When passed, the SVG Logo element is wrapped in a HTML <a> tag and this
   * `href` attribute is set on that <a> element.
   */
  href?: string;

  /**
   * When passed, and `href` us also passes, then this is used as the label for the link that is rendered.
   * However, this label is not visible, and only part of the DOM for screen readers and for DEO purpose.
   */
  linkLabel?: string;

  /**
   * These props are passed as it is to the HTML <a> element that's rendered when the
   * `href` prop is set. It is ignored otherwise.
   */
  linkProps?: any;

  /**
   * These props are passes as it is to the HTML <svg> element representing the logo.
   */
  svgProps?: React.SVGProps<SVGSVGElement>;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop.
   */
  className?: string;
}

/**
 * This component represent the Frontdoor brand logo is to be rendered.
 * Consuming application should always use this component whenever they wish to
 * render any Frontdoor brand logo.
 *
 * @deprecated Use individual logo components instead.
 */
export const Logo = ({
  brand = "ahs",
  variant = "logo",
  monochrome = false,
  inverse = false,
  onClick,
  href,
  linkLabel,
  linkProps = {},
  svgProps = {},
  className: classNameProp,
  ...restProps
}: LogoProps) => {
  const className = useMemo(
    () =>
      classNames(
        {
          "cursor-pointer": onClick,
          "text-primary": monochrome && !inverse,
          "text-white": monochrome && inverse,
        },
        classNameProp
      ),
    [classNameProp, inverse, monochrome, onClick]
  );

  const LogoComponent = useMemo(
    () => logoComponentMap[brand][variant][monochrome ? "mono" : "color"],
    [brand, monochrome, variant]
  );

  if (LogoComponent) {
    if (href) {
      return (
        <a href={href} className={className} onClick={onClick} {...linkProps}>
          <LogoComponent {...svgProps} {...restProps} />
          {linkLabel && <span className="sr-only">{linkLabel}</span>}
        </a>
      );
    } else {
      return <LogoComponent onClick={onClick} className={className} {...svgProps} {...restProps} />;
    }
  } else {
    return null;
  }
};
